// ═══════════════════════════════════════════════════════════════
// Perimeter Module — Evacuation Route Analysis
// 1:1 port from perimeter-embedded.html analyzeEvacRoutes / suggestStaging
// ═══════════════════════════════════════════════════════════════

import { getCardinalDirection, type LatLng, type PerimeterPOI, type PerimeterClosure } from './perimeterGeo';

// ── Types ────────────────────────────────────────────────────

export type Suitability = 'high' | 'medium' | 'low';

export interface EvacuationRoute {
  name: string;
  direction: string;
  roadType: string;
  lat: number;
  lng: number;
  distance: number;
  suitability: Suitability;
}

export interface StagingArea {
  name: string;
  category: string;
  distance: number;
  lat: number;
  lng: number;
  direction: string;
  isOutside: boolean;
  reason: string;
}

// ── Analyze Evacuation Routes ────────────────────────────────

export function analyzeEvacuationRoutes(closures: PerimeterClosure[]): EvacuationRoute[] {
  const routes: EvacuationRoute[] = closures.map(c => ({
    name: c.name,
    direction: c.direction,
    roadType: c.roadType,
    lat: c.lat,
    lng: c.lng,
    distance: c.distance,
    suitability: c.priority <= 2 ? 'high' : c.priority === 3 ? 'medium' : 'low',
  }));

  // Sort by suitability: high → medium → low
  const suitabilityOrder: Record<Suitability, number> = { high: 0, medium: 1, low: 2 };
  routes.sort((a, b) => suitabilityOrder[a.suitability] - suitabilityOrder[b.suitability]);

  return routes;
}

// ── Suggest Staging Areas ────────────────────────────────────

export function suggestStagingAreas(
  pois: PerimeterPOI[],
  center: LatLng,
  radius: number
): StagingArea[] {
  const goodCategories = ['school', 'worship', 'supermarket', 'government', 'shelter'];

  const reasonMap: Record<string, string> = {
    school: 'Gymnasium, large parking lot, open fields',
    worship: 'Indoor assembly space, parking lot',
    supermarket: 'Large parking lot, supplies, cold storage',
    government: 'Government facility, established infrastructure',
    shelter: 'Emergency shelter facility',
  };

  // Score facilities for suitability
  const scored = pois
    .filter(p => goodCategories.includes(p.category))
    .map(p => {
      let score = 0;
      if (p.distance > radius) score += 10; // outside perimeter preferred
      if (p.category === 'school') score += 5;
      if (p.category === 'worship') score += 4;
      if (p.category === 'supermarket') score += 3;
      if (p.category === 'government') score += 2;
      if (p.category === 'shelter') score += 6;
      // Prefer facilities 60%-150% of radius (near perimeter)
      const ratio = p.distance / radius;
      if (ratio >= 0.6 && ratio <= 1.5) score += 3;
      return { poi: p, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 15);

  return scored.map(({ poi: p }) => ({
    name: p.name,
    category: p.categoryLabel || p.category,
    distance: p.distance,
    lat: p.lat,
    lng: p.lng,
    direction: getCardinalDirection(center.lat, center.lng, p.lat, p.lng),
    isOutside: p.distance > radius,
    reason: reasonMap[p.category] || 'Suitable facility',
  }));
}
